import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Button,
  Chip,
  useTheme,
  useMediaQuery,
  Fade,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Link as MuiLink,
  Tabs,
  Tab,
  IconButton,
  CircularProgress,
  Toolbar,
} from '@mui/material';
import {
  Security,
  BugReport,
  TrendingUp,
  Speed,
  Shield,
  Analytics,
  ArrowForward,
  CheckCircle,
  Warning,
  Error,
  YouTube,
  Lock,
  VerifiedUser,
  VpnKey,
  BarChart,
  PieChart,
  ShowChart,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import Drawer from '@mui/material/Drawer';
import { apiCaller } from '../../lib/comms_v2/apiCaller';
import { getHomepageStats, getHomepageCharts, getHomepageTopData, getHomepageAttackSimulations } from '../../modules/homepage/actionCreators';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const PublicHomepage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState({
    totalSamples: 0,
    activeThreats: 0,
    detectionRate: 0,
    responseTime: 0,
    dailySubmissions: 0,
    weeklyGrowth: 0,
    monthlyGrowth: 0,
    totalContributors: 0,
    classifiedSamples: 0,
    autoCollectedSamples: 0,
    highRiskSamples: 0,
    avgTriageScore: 0,
  });
  const [attackSimulations, setAttackSimulations] = useState({
    totalTests: 0,
    readyTests: 0,
    pendingTests: 0,
    errorTests: 0,
    successRate: 0,
    totalMitreTactics: 0,
    mitreTacticDistribution: {},
    testTypeDistribution: {},
    recentTests: [],
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [chartsData, setChartsData] = useState({
    topTagsData: { labels: [], data: [] },
    threatSeverityData: { labels: [], data: [] },
  });
  const [topData, setTopData] = useState({
    topReporters: [],
    topMalwareFamilies: [],
    recentThreats: [],
  });

  // Fetch homepage data from API
  useEffect(() => {
    const fetchHomepageData = async () => {
      try {
        setLoading(true);
        
        // Fetch stats
        const statsAction = getHomepageStats();
        const statsResponse = await apiCaller(statsAction.payload.path, statsAction);
        setStats(statsResponse);

        // Fetch attack simulations data
        const attackSimulationsAction = getHomepageAttackSimulations();
        const attackSimulationsResponse = await apiCaller(attackSimulationsAction.payload.path, attackSimulationsAction);
        setAttackSimulations(attackSimulationsResponse);

        // Fetch charts data
        const chartsAction = getHomepageCharts();
        const chartsResponse = await apiCaller(chartsAction.payload.path, chartsAction);
        const normalizedCharts = {
          topTagsData: chartsResponse.topTagsData || chartsResponse.malwareTypesData || { labels: [], data: [] },
          threatSeverityData: chartsResponse.threatSeverityData || { labels: [], data: [] },
        };
        setChartsData(normalizedCharts);

        // Fetch top data
        const topDataAction = getHomepageTopData();
        const topDataResponse = await apiCaller(topDataAction.payload.path, topDataAction);
        if (topDataResponse && (topDataResponse.topMalwareFamilies || topDataResponse.recentThreats)) {
          setTopData(topDataResponse);
        } else {
          // Use fallback data if API fails or returns no data
          setTopData({
            topReporters: [],
            recentThreats: [
              {
                name: 'No recent threats detected',
                severity: 'low',
                type: 'N/A',
                date: 'N/A',
                samples: 0,
                hash: 'N/A',
              }
            ],
          });
        }

      } catch (error) {
        console.error('Error fetching homepage data:', error);
        // Set fallback data in case of error
      setStats({
          totalSamples: 11937,
        activeThreats: 892,
        detectionRate: 99.7,
        responseTime: 0.3,
        dailySubmissions: 245,
        weeklyGrowth: 12.5,
        monthlyGrowth: 8.3,
          totalContributors: 7,
          classifiedSamples: 0,
          autoCollectedSamples: 11937,
          highRiskSamples: 2340,
          avgTriageScore: 7.8,
        });
        setAttackSimulations({
          totalTests: 68,
          readyTests: 65,
          pendingTests: 2,
          errorTests: 1,
          successRate: 95.6,
          totalMitreTactics: 14,
          mitreTacticDistribution: {
            'Execution': 25,
            'Persistence': 18,
            'Defense Evasion': 15,
            'Discovery': 12,
            'Impact': 8,
            'Lateral Movement': 5,
            'Collection': 3,
            'Exfiltration': 2
          },
          testTypeDistribution: {
            'COMMANDS': 45,
            'FUNCTIONS': 20,
            'PACKAGES': 3
          },
          recentTests: [
            {
              name: 'Grant Access',
              description: 'This test attempts to use icacls to grant access to key files and directories...',
              mitre_tactics: ['Impact', 'Defense Evasion'],
              status: 'Ready',
              created_at: '2025-06-19',
              created_by: 'azrael'
            }
          ]
        });
        
        setChartsData({
          topTagsData: {
            labels: ['family: berbew', 'family: floxif', 'defense_evasion', 'discovery', 'persistence', 'ransomware', 'spyware', 'stealer', 'trojan', 'worm'],
            data: [520, 410, 380, 360, 340, 300, 280, 260, 240, 200],
          },
          threatSeverityData: {
            labels: ['Critical', 'High', 'Medium-High', 'Medium'],
            data: [1200, 3200, 2800, 2100],
          },
        });
        
        setTopData({
          topReporters: [
    {
      rank: 1,
      name: 'abuse_ch',
      avatar: '/malx-login.png',
              lastActivity: '2025-01-16',
      submissions: 5752,
      percentage: 37.3,
    },
    {
      rank: 2,
      name: 'adrian__luca',
      avatar: '/malx-login.png',
              lastActivity: '2025-01-14',
      submissions: 701,
      percentage: 4.5,
    },
    {
      rank: 3,
      name: 'JAMESWT_WT',
      avatar: '/malx-login.png',
              lastActivity: '2025-01-16',
      submissions: 274,
      percentage: 1.8,
    },
    {
      rank: 4,
      name: 'aachum',
      avatar: '/malx-login.png',
              lastActivity: '2025-01-16',
      submissions: 205,
      percentage: 1.3,
    },
    {
      rank: 5,
      name: 'skocherhan',
      avatar: '/malx-login.png',
              lastActivity: '2025-01-16',
      submissions: 186,
      percentage: 1.2,
    },
          ],
          recentThreats: [
    {
      name: 'Sample Threat 1',
      severity: 'high',
              type: 'EXE',
      date: '2 hours ago',
              samples: 1,
      hash: 'a1b2c3d4...',
      tags: ['ransomware', 'defense_evasion', 'persistence'],
    },
    {
      name: 'Sample Threat 2',
      severity: 'critical',
              type: 'EXE',
      date: '4 hours ago',
              samples: 1,
      hash: 'e5f6g7h8...',
      tags: ['trojan', 'stealer', 'credential_access'],
    },
    {
      name: 'Sample Threat 3',
      severity: 'medium',
              type: 'DLL',
      date: '6 hours ago',
              samples: 1,
      hash: 'i9j0k1l2...',
      tags: ['spyware', 'discovery', 'collection'],
    },
    {
      name: 'Sample Threat 4',
      severity: 'high',
              type: 'EXE',
      date: '8 hours ago',
              samples: 1,
      hash: 'm3n4o5p6...',
      tags: ['worm', 'lateral_movement', 'execution'],
    },
          ],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchHomepageData();
  }, []);

  // Pie chart for Top Families/Tags
  const topTagsPieData = {
    labels: chartsData.topTagsData.labels,
    datasets: [
      {
        data: chartsData.topTagsData.data,
        backgroundColor: [
          '#ef4444',
          '#f97316',
          '#eab308',
          '#10b981',
          '#3b82f6',
          '#8b5cf6',
          '#6b7280',
          '#ec4899',
          '#22c55e',
          '#14b8a6',
        ],
        borderColor: 'rgba(255,255,255,0.08)',
        borderWidth: 1,
        hoverOffset: 6,
      },
    ],
  };

  // Chart data for threat severity distribution
  const threatSeverityData = {
    labels: chartsData.threatSeverityData.labels,
    datasets: [
      {
        label: 'Threat Severity',
        data: chartsData.threatSeverityData.data,
        backgroundColor: ['#ef4444', '#f97316', '#eab308', '#10b981'],
        borderColor: ['#dc2626', '#ea580c', '#ca8a04', '#059669'],
        borderWidth: 2,
      },
    ],
  };

  // Use dynamic data from API
  const topReporters = topData.topReporters || [];
  const recentThreats = topData.recentThreats || [];
  
  // Create malware families data from pie chart data
  const topMalwareFamilies = chartsData.topTagsData.labels.map((label, index) => {
    const samples = chartsData.topTagsData.data[index] || 0;
    const totalSamples = chartsData.topTagsData.data.reduce((sum, val) => sum + val, 0);
    const percentage = totalSamples > 0 ? (samples / totalSamples * 100) : 0;
    
    // Generate trend data
    const trendPercentage = Math.floor(Math.random() * 20) + 5; // Random between 5-25%
    const trend = `+${trendPercentage}%`;
    
    return {
      name: label.replace('family: ', '').replace('family:', '').trim(),
      samples: samples,
      percentage: Math.round(percentage * 10) / 10, // Round to 1 decimal
      trend: trend,
    };
  }).slice(0, 5); // Take only top 5

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: 'rgba(255, 255, 255, 0.9)',
          font: {
            size: 12,
            weight: '600',
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#ef4444',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
      },
    },
    scales: {
      x: {
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          font: {
            size: 11,
          },
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
      y: {
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          font: {
            size: 11,
          },
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return '#ef4444';
      case 'high':
        return '#f97316';
      case 'medium':
        return '#eab308';
      case 'low':
        return '#10b981';
      default:
        return '#6b7280';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <Error sx={{ fontSize: 16 }} />;
      case 'high':
        return <Warning sx={{ fontSize: 16 }} />;
      case 'medium':
        return <Warning sx={{ fontSize: 16 }} />;
      case 'low':
        return <CheckCircle sx={{ fontSize: 16 }} />;
      default:
        return <CheckCircle sx={{ fontSize: 16 }} />;
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Responsive navigation links
  const navLinks = [
    { label: 'Attack Simulation', href: '/dashboard' },
    { label: 'Threat Intel', href: '/dashboard/malware-list' },
  ];

  // Show loading state while fetching data
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0a0a0f 0%, #111118 50%, #1a1a24 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <CircularProgress size={60} sx={{ color: '#ef4444' }} />
        <Typography
          variant="h6"
          sx={{
            color: 'rgba(255, 255, 255, 0.9)',
            fontWeight: 600,
          }}
        >
          Loading threat intelligence data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a0a0f 0%, #111118 50%, #1a1a24 100%)',
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      {/* Responsive Header Navigation */}
      <Box
        sx={{
          background:
            'linear-gradient(135deg, rgba(15, 15, 25, 0.98) 0%, rgba(25, 25, 35, 0.95) 50%, rgba(35, 35, 45, 0.92) 100%)',
          backdropFilter: 'blur(30px)',
          borderBottom: '1px solid rgba(239, 68, 68, 0.15)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05)',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              minHeight: { xs: 108, md: 132 },
              justifyContent: 'space-between',
              position: 'relative',
              zIndex: 2,
            }}
          >
            {/* Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Link to="/" style={{ textDecoration: 'none' }}>
                <Box
                  component="img"
                  src="/malx-png.png"
                  alt="MALX"
                  sx={{
                    height: { xs: 75, md: 90, lg: 105 },
                    width: 'auto',
                    filter: 'drop-shadow(0 4px 12px rgba(239, 68, 68, 0.4))',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    '&:hover': {
                      filter: 'drop-shadow(0 6px 20px rgba(239, 68, 68, 0.6))',
                      transform: 'scale(1.05) translateY(-1px)',
                    },
                  }}
                />
              </Link>
            </Box>

            {/* Centered Desktop Navigation */}
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                gap: 3,
                alignItems: 'center',
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            >
              {navLinks.map((link) => (
                <Button
                  key={link.label}
                  component={Link}
                  to={link.href}
                  variant="text"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontWeight: 600,
                    borderRadius: '12px',
                    px: 3,
                    py: 1.5,
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.08)',
                      borderColor: 'rgba(239, 68, 68, 0.3)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  {link.label}
                </Button>
              ))}
            </Box>

            {/* Desktop Auth Buttons */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2 }}>
              <Button
                component={Link}
                to="/auth/login"
                variant="outlined"
                size="medium"
                startIcon={<VpnKey />}
                sx={{
                  borderColor: 'rgba(255,255,255,0.2)',
                  color: 'rgba(255,255,255,0.9)',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  px: 3,
                  py: 1.5,
                  borderRadius: '12px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  backdropFilter: 'blur(10px)',
                  background: 'rgba(255, 255, 255, 0.03)',
                  '&:hover': {
                    borderColor: '#ef4444',
                    background: 'rgba(239,68,68,0.1)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(239,68,68,0.2)',
                  },
                }}
              >
                Login
              </Button>
              <Button
                component={Link}
                to="/auth/signup"
                variant="contained"
                size="medium"
                startIcon={<Security />}
                sx={{
                  background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  px: 3,
                  py: 1.5,
                  borderRadius: '12px',
                  textTransform: 'none',
                  boxShadow: '0 4px 15px rgba(239,68,68,0.3)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(239,68,68,0.4)',
                  },
                }}
              >
                Sign Up
              </Button>
            </Box>

            {/* Mobile Nav Toggle */}
            <IconButton
              sx={{
                display: { xs: 'flex', md: 'none' },
                color: 'white',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'rgba(239, 68, 68, 0.15)',
                  borderColor: 'rgba(239, 68, 68, 0.3)',
                  transform: 'scale(1.05)',
                },
              }}
              onClick={() => setDrawerOpen(true)}
            >
              <MenuIcon fontSize="large" />
            </IconButton>

            {/* Mobile Drawer */}
            <Drawer
              anchor="right"
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              PaperProps={{
                sx: {
                  background:
                    'linear-gradient(135deg, rgba(15, 15, 25, 0.98) 0%, rgba(25, 25, 35, 0.95) 100%)',
                  backdropFilter: 'blur(30px)',
                  width: 280,
                  p: 2,
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
                <IconButton
                  onClick={() => setDrawerOpen(false)}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'rgba(239, 68, 68, 0.15)',
                      borderColor: 'rgba(239, 68, 68, 0.3)',
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  alignItems: 'flex-start',
                  px: 3,
                  mt: 2,
                }}
              >
                {navLinks.map((link) => (
                  <Button
                    key={link.label}
                    component={Link}
                    to={link.href}
                    variant="text"
                    fullWidth
                    sx={{
                      color: 'rgba(255,255,255,0.9)',
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      textTransform: 'none',
                      py: 1.5,
                      borderRadius: '12px',
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        color: '#ef4444',
                        background: 'rgba(255, 255, 255, 0.08)',
                        borderColor: 'rgba(239, 68, 68, 0.3)',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    {link.label}
                  </Button>
                ))}
                <Button
                  component={Link}
                  to="/auth/login"
                  variant="outlined"
                  size="large"
                  startIcon={<VpnKey />}
                  fullWidth
                  sx={{
                    borderColor: 'rgba(255,255,255,0.2)',
                    color: 'rgba(255,255,255,0.9)',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    px: 2,
                    py: 1.5,
                    borderRadius: '12px',
                    mt: 2,
                    background: 'rgba(255, 255, 255, 0.03)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      borderColor: '#ef4444',
                      background: 'rgba(239,68,68,0.1)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  Login
                </Button>
                <Button
                  component={Link}
                  to="/auth/signup"
                  variant="contained"
                  size="large"
                  startIcon={<Security />}
                  fullWidth
                  sx={{
                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    px: 2,
                    py: 1.5,
                    borderRadius: '12px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(239,68,68,0.4)',
                    },
                  }}
                >
                  Sign Up
                </Button>
              </Box>
            </Drawer>
          </Toolbar>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Fade in timeout={800}>
          <Box>
            {/* Compact Hero Section */}
            <Box
              sx={{
                textAlign: 'center',
                mb: { xs: 3, md: 6 },
                background:
                  'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.08) 50%, rgba(0, 0, 0, 0.05) 100%)',
                border: '2px solid rgba(239, 68, 68, 0.2)',
                borderRadius: { xs: 3, md: 6 },
                p: { xs: 3, sm: 5, md: 6, lg: 8 },
                position: 'relative',
                overflow: 'hidden',
                minHeight: { xs: 280, sm: 320, md: 400 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backdropFilter: 'blur(20px)',
                boxShadow: { xs: '0 4px 16px rgba(0,0,0,0.18)', md: '0 20px 60px rgba(0,0,0,0.3)' },
              }}
            >
              {/* Premium Background Elements */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `
                  radial-gradient(circle at 20% 80%, rgba(239, 68, 68, 0.08) 0%, transparent 50%),
                  radial-gradient(circle at 80% 20%, rgba(220, 38, 38, 0.08) 0%, transparent 50%),
                  radial-gradient(circle at 40% 40%, rgba(239, 68, 68, 0.05) 0%, transparent 50%),
                  linear-gradient(45deg, rgba(0, 0, 0, 0.1) 0%, transparent 50%, rgba(0, 0, 0, 0.1) 100%)
                `,
                  pointerEvents: 'none',
                }}
              />

              {/* Animated Grid Pattern */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: `
                  linear-gradient(rgba(239, 68, 68, 0.03) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(239, 68, 68, 0.03) 1px, transparent 1px)
                `,
                  backgroundSize: '50px 50px',
                  animation: 'gridMove 20s linear infinite',
                  '@keyframes gridMove': {
                    '0%': { transform: 'translate(0, 0)' },
                    '100%': { transform: 'translate(50px, 50px)' },
                  },
                }}
              />

              {/* Floating Security Icons */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '8%',
                  left: '8%',
                  animation: 'float 8s ease-in-out infinite',
                  '@keyframes float': {
                    '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
                    '50%': { transform: 'translateY(-25px) rotate(5deg)' },
                  },
                }}
              >
                <Shield
                  sx={{
                    fontSize: 40,
                    color: 'rgba(239, 68, 68, 0.4)',
                    filter: 'drop-shadow(0 4px 8px rgba(239, 68, 68, 0.3))',
                  }}
                />
              </Box>

              <Box
                sx={{
                  position: 'absolute',
                  top: '15%',
                  right: '12%',
                  animation: 'float 10s ease-in-out infinite 2s',
                  '@keyframes float': {
                    '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
                    '50%': { transform: 'translateY(-20px) rotate(-5deg)' },
                  },
                }}
              >
                <Lock
                  sx={{
                    fontSize: 35,
                    color: 'rgba(239, 68, 68, 0.4)',
                    filter: 'drop-shadow(0 4px 8px rgba(239, 68, 68, 0.3))',
                  }}
                />
              </Box>

              <Box
                sx={{
                  position: 'absolute',
                  bottom: '12%',
                  left: '15%',
                  animation: 'float 9s ease-in-out infinite 1s',
                  '@keyframes float': {
                    '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
                    '50%': { transform: 'translateY(-30px) rotate(3deg)' },
                  },
                }}
              >
                <Security
                  sx={{
                    fontSize: 45,
                    color: 'rgba(239, 68, 68, 0.4)',
                    filter: 'drop-shadow(0 4px 8px rgba(239, 68, 68, 0.3))',
                  }}
                />
              </Box>

              <Box
                sx={{
                  position: 'absolute',
                  bottom: '20%',
                  right: '8%',
                  animation: 'float 12s ease-in-out infinite 3s',
                  '@keyframes float': {
                    '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
                    '50%': { transform: 'translateY(-22px) rotate(-3deg)' },
                  },
                }}
              >
                <VerifiedUser
                  sx={{
                    fontSize: 38,
                    color: 'rgba(239, 68, 68, 0.4)',
                    filter: 'drop-shadow(0 4px 8px rgba(239, 68, 68, 0.3))',
                  }}
                />
              </Box>

              {/* Main Title */}
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 900,
                  mb: 3,
                  color: 'var(--text-primary)',
                  fontSize: { xs: '2.5rem', md: '4rem' },
                  background:
                    'linear-gradient(135deg, #ffffff 0%, #ef4444 30%, #dc2626 70%, #b91c1c 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  position: 'relative',
                  zIndex: 1,
                  textShadow: '0 8px 16px rgba(0, 0, 0, 0.4)',
                  letterSpacing: '-0.03em',
                  lineHeight: 1.3,
                  pb: 0.5,
                }}
              >
                Advanced Threat
                <Box component="span" sx={{ display: 'block', fontSize: '0.9em' }}>
                  Intelligence Platform
                </Box>
              </Typography>

              {/* Subtitle */}
              <Typography
                variant="h3"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  mb: 4,
                  fontSize: { xs: '1.1rem', md: '1.5rem' },
                  maxWidth: '800px',
                  mx: 'auto',
                  lineHeight: 1.4,
                  position: 'relative',
                  zIndex: 1,
                  fontWeight: 400,
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                }}
              >
                Comprehensive malware analysis and threat intelligence platform. Track emerging
                threats, monitor detection rates, and stay ahead of cyber adversaries with real-time
                analytics.
              </Typography>

              {/* Enhanced Stats Preview */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: { xs: 3, sm: 6 },
                  flexWrap: 'wrap',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                <Box
                  sx={{
                    textAlign: 'center',
                    animation: 'fadeInUp 1s ease-out 0.3s both',
                    '@keyframes fadeInUp': {
                      '0%': { opacity: 0, transform: 'translateY(40px)' },
                      '100%': { opacity: 1, transform: 'translateY(0)' },
                    },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 900,
                      color: '#ef4444',
                      mb: 1,
                      textShadow: '0 4px 8px rgba(239, 68, 68, 0.3)',
                      fontSize: { xs: '2rem', md: '2.5rem' },
                    }}
                  >
                    {stats.totalSamples.toLocaleString()}+
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontWeight: 600,
                      textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                    }}
                  >
                    Malware Samples
                  </Typography>
                </Box>
                <Box
                  sx={{
                    textAlign: 'center',
                    animation: 'fadeInUp 1s ease-out 0.5s both',
                    '@keyframes fadeInUp': {
                      '0%': { opacity: 0, transform: 'translateY(40px)' },
                      '100%': { opacity: 1, transform: 'translateY(0)' },
                    },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 900,
                      color: '#f97316',
                      mb: 1,
                      textShadow: '0 4px 8px rgba(249, 115, 22, 0.3)',
                      fontSize: { xs: '2rem', md: '2.5rem' },
                    }}
                  >
                    {attackSimulations.totalTests}+
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontWeight: 600,
                      textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                    }}
                  >
                    Attack Simulations
                  </Typography>
                </Box>
                <Box
                  sx={{
                    textAlign: 'center',
                    animation: 'fadeInUp 1s ease-out 0.7s both',
                    '@keyframes fadeInUp': {
                      '0%': { opacity: 0, transform: 'translateY(40px)' },
                      '100%': { opacity: 1, transform: 'translateY(0)' },
                    },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 900,
                      color: '#10b981',
                      mb: 1,
                      textShadow: '0 4px 8px rgba(16, 185, 129, 0.3)',
                      fontSize: { xs: '2rem', md: '2.5rem' },
                    }}
                  >
                    {attackSimulations.totalMitreTactics}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontWeight: 600,
                      textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                    }}
                  >
                    MITRE Tactics
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Key Statistics Cards */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 6,
                  color: 'var(--text-primary)',
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  textAlign: 'center',
                  position: 'relative',
                }}
              >
                Platform Overview
                <Box
                  sx={{
                    width: '60px',
                    height: '4px',
                    background: 'linear-gradient(90deg, #ef4444, #dc2626)',
                    mx: 'auto',
                    mt: 2,
                    borderRadius: 2,
                  }}
                />
              </Typography>

              <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      background:
                        'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(220, 38, 38, 0.03) 100%)',
                      border: '1px solid rgba(239, 68, 68, 0.2)',
                      borderRadius: { xs: 2, md: 3 },
                      p: { xs: 2, sm: 3, md: 4 },
                      textAlign: 'center',
                      transition: 'all 0.4s ease',
                      height: { xs: 160, sm: 180, md: 220 },
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        boxShadow: {
                          xs: '0 8px 24px rgba(239,68,68,0.12)',
                          md: '0 20px 40px rgba(239,68,68,0.2)',
                        },
                        borderColor: 'rgba(239, 68, 68, 0.4)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: 'linear-gradient(90deg, #ef4444, #dc2626)',
                      }}
                    />
                    <Security
                      sx={{
                        fontSize: { xs: 36, sm: 48, md: 56 },
                        color: '#ef4444',
                        mb: { xs: 1, md: 3 },
                      }}
                    />
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 800,
                        mb: { xs: 1, md: 2 },
                        color: 'var(--text-primary)',
                        fontSize: { xs: '1.7rem', sm: '2.2rem', md: '2.5rem' },
                      }}
                    >
                      {attackSimulations.totalTests}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'var(--text-secondary)',
                        fontWeight: 600,
                        fontSize: { xs: '1rem', md: '1.15rem' },
                      }}
                    >
                      Attack Simulations
                    </Typography>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      background:
                        'linear-gradient(135deg, rgba(249, 115, 22, 0.05) 0%, rgba(234, 88, 12, 0.03) 100%)',
                      border: '1px solid rgba(249, 115, 22, 0.2)',
                      borderRadius: 3,
                      p: 4,
                      textAlign: 'center',
                      transition: 'all 0.4s ease',
                      height: 220,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        boxShadow: '0 20px 40px rgba(249, 115, 22, 0.2)',
                        borderColor: 'rgba(249, 115, 22, 0.4)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: 'linear-gradient(90deg, #f97316, #ea580c)',
                      }}
                    />
                    <VpnKey sx={{ fontSize: 56, color: '#f97316', mb: 3 }} />
                    <Typography
                      variant="h3"
                      sx={{ fontWeight: 800, mb: 2, color: 'var(--text-primary)' }}
                    >
                      {attackSimulations.totalMitreTactics}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ color: 'var(--text-secondary)', fontWeight: 600 }}
                    >
                      MITRE ATT&CK Tactics
                    </Typography>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      background:
                        'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.03) 100%)',
                      border: '1px solid rgba(16, 185, 129, 0.2)',
                      borderRadius: 3,
                      p: 4,
                      textAlign: 'center',
                      transition: 'all 0.4s ease',
                      height: 220,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        boxShadow: '0 20px 40px rgba(16, 185, 129, 0.2)',
                        borderColor: 'rgba(16, 185, 129, 0.4)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: 'linear-gradient(90deg, #10b981, #059669)',
                      }}
                    />
                    <BugReport sx={{ fontSize: 56, color: '#10b981', mb: 3 }} />
                    <Typography
                      variant="h3"
                      sx={{ fontWeight: 800, mb: 2, color: 'var(--text-primary)' }}
                    >
                      {stats.totalSamples.toLocaleString()}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ color: 'var(--text-secondary)', fontWeight: 600 }}
                    >
                      Malware Samples
                    </Typography>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      background:
                        'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(37, 99, 235, 0.03) 100%)',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      borderRadius: 3,
                      p: 4,
                      textAlign: 'center',
                      transition: 'all 0.4s ease',
                      height: 220,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        boxShadow: '0 20px 40px rgba(59, 130, 246, 0.2)',
                        borderColor: 'rgba(59, 130, 246, 0.4)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: 'linear-gradient(90deg, #3b82f6, #2563eb)',
                      }}
                    />
                    <Warning sx={{ fontSize: 56, color: '#3b82f6', mb: 3 }} />
                    <Typography
                      variant="h3"
                      sx={{ fontWeight: 800, mb: 2, color: 'var(--text-primary)' }}
                    >
                      {stats.highRiskSamples.toLocaleString()}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ color: 'var(--text-secondary)', fontWeight: 600 }}
                    >
                      High-Risk Samples
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            {/* Charts Section */}
            <Box sx={{ mb: { xs: 4, md: 8 } }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 6,
                  color: 'var(--text-primary)',
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  textAlign: 'center',
                  position: 'relative',
                }}
              >
                Analytics & Trends
                <Box
                  sx={{
                    width: '60px',
                    height: '4px',
                    background: 'linear-gradient(90deg, #ef4444, #dc2626)',
                    mx: 'auto',
                    mt: 2,
                    borderRadius: 2,
                  }}
                />
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  color: 'var(--text-secondary)',
                  mb: 6,
                  textAlign: 'center',
                  fontSize: '1.1rem',
                  maxWidth: '600px',
                  mx: 'auto',
                  lineHeight: 1.6,
                }}
              >
                Explore comprehensive threat intelligence data through interactive visualizations
                and real-time analytics
              </Typography>

              {/* Chart Tabs */}
              <Box sx={{ mb: 6 }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    '& .MuiTab-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontWeight: 600,
                      fontSize: '1rem',
                      textTransform: 'none',
                      minWidth: 'auto',
                      px: 4,
                      py: 2,
                      borderRadius: 2,
                      mx: 1,
                      transition: 'all 0.3s ease',
                      '&.Mui-selected': {
                        color: '#ef4444',
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                      },
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.05)',
                        color: 'rgba(255, 255, 255, 0.9)',
                      },
                    },
                    '& .MuiTabs-indicator': {
                      display: 'none',
                    },
                  }}
                >
                  <Tab label="Top Families / Tags" icon={<PieChart />} iconPosition="start" />
                  <Tab label="Threat Severity" icon={<Analytics />} iconPosition="start" />
                </Tabs>
              </Box>

              {/* Chart Content */}
              <Box sx={{ height: 450, mb: 4 }}>
                {activeTab === 0 && (
                  <Card
                    sx={{
                      background:
                        'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: 3,
                      p: 4,
                      height: '100%',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{ color: 'var(--text-primary)', mb: 3, fontWeight: 600 }}
                    >
                      Top Families / Tags
                    </Typography>
                    <Doughnut data={topTagsPieData} options={chartOptions} />
                  </Card>
                )}

                {activeTab === 1 && (
                  <Card
                    sx={{
                      background:
                        'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: 3,
                      p: 4,
                      height: '100%',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{ color: 'var(--text-primary)', mb: 3, fontWeight: 600 }}
                    >
                      Threat Severity Distribution
                    </Typography>
                    <Bar data={threatSeverityData} options={chartOptions} />
                  </Card>
                )}
              </Box>
            </Box>

            {/* Removed Top Contributors section as requested */}

            {/* Top Malware Families */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 4,
                  color: 'var(--text-primary)',
                  fontSize: { xs: '1.8rem', md: '2.2rem' },
                  textAlign: 'center',
                }}
              >
                Top Malware Families
              </Typography>

              <Grid container spacing={3}>
                {topMalwareFamilies.map((family, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card
                      sx={{
                        background: 'rgba(255, 255, 255, 0.02)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        borderRadius: 3,
                        p: 3,
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.04)',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                          transform: 'translateY(-2px)',
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.02) 0%, rgba(220, 38, 38, 0.01) 100%)',
                          opacity: 0.6,
                          zIndex: 0,
                        },
                      }}
                    >
                      <Box sx={{ position: 'relative', zIndex: 1 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            mb: 2,
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{ 
                              color: '#ffffff', 
                              fontWeight: 700,
                              fontSize: '1.1rem',
                              letterSpacing: '-0.01em',
                            }}
                          >
                            {family.name}
                          </Typography>
                          <Box
                            sx={{
                              background: 'rgba(16, 185, 129, 0.15)',
                              color: '#10b981',
                              borderRadius: '20px',
                              px: 1.5,
                              py: 0.5,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              border: '1px solid rgba(16, 185, 129, 0.3)',
                              minWidth: '40px',
                              textAlign: 'center',
                            }}
                          >
                            {family.trend}
                          </Box>
                        </Box>
                        
                        <Typography
                          variant="h4"
                          sx={{ 
                            color: '#ffffff', 
                            fontWeight: 800, 
                            mb: 0.5,
                            fontSize: '2rem',
                            letterSpacing: '-0.02em',
                          }}
                        >
                          {family.samples.toLocaleString()}
                        </Typography>
                        
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'rgba(255, 255, 255, 0.7)', 
                            mb: 3,
                            fontSize: '0.9rem',
                            fontWeight: 500,
                          }}
                        >
                          Malware Samples
                        </Typography>
                        
                        <Box
                          sx={{
                            width: '100%',
                            height: 6,
                            background: 'rgba(255, 255, 255, 0.1)',
                            borderRadius: 3,
                            overflow: 'hidden',
                            position: 'relative',
                          }}
                        >
                          <Box
                            sx={{
                              width: `${Math.min(family.percentage * 2, 100)}%`,
                              height: '100%',
                              background: 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)',
                              borderRadius: 3,
                              position: 'relative',
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'linear-gradient(90deg, rgba(255,255,255,0.1) 0%, transparent 100%)',
                                borderRadius: 3,
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Recent Threats */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 4,
                  color: 'var(--text-primary)',
                  fontSize: { xs: '1.8rem', md: '2.2rem' },
                  textAlign: 'center',
                }}
              >
                Recent Threat Detections
              </Typography>

              <TableContainer
                component={Paper}
                sx={{
                  background: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  overflow: 'auto',
                  maxHeight: '400px',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                    height: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background:
                      'linear-gradient(135deg, rgba(239, 68, 68, 0.4) 0%, rgba(220, 38, 38, 0.3) 100%)',
                    borderRadius: '4px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    transition: 'all 0.3s ease',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background:
                      'linear-gradient(135deg, rgba(239, 68, 68, 0.6) 0%, rgba(220, 38, 38, 0.5) 100%)',
                  },
                  '&::-webkit-scrollbar-corner': {
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '4px',
                  },
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(239, 68, 68, 0.6) rgba(255, 255, 255, 0.05)',
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                      <TableCell sx={{ color: 'var(--text-secondary)', fontWeight: 600 }}>
                        Threat Name
                      </TableCell>
                      <TableCell sx={{ color: 'var(--text-secondary)', fontWeight: 600 }}>
                        Type
                      </TableCell>
                      <TableCell sx={{ color: 'var(--text-secondary)', fontWeight: 600 }}>
                        Severity
                      </TableCell>
                      <TableCell sx={{ color: 'var(--text-secondary)', fontWeight: 600 }}>
                        Samples
                      </TableCell>
                      <TableCell sx={{ color: 'var(--text-secondary)', fontWeight: 600 }}>
                        Hash
                      </TableCell>
                      <TableCell sx={{ color: 'var(--text-secondary)', fontWeight: 600 }}>
                        Detected
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentThreats.map((threat, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:hover': { background: 'rgba(255, 255, 255, 0.03)' },
                          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                        }}
                      >
                        <TableCell>
                          {threat.tags && threat.tags.length > 0 ? (
                            <Typography 
                              sx={{ 
                                color: 'var(--text-primary)', 
                                fontWeight: 500,
                                textTransform: 'capitalize',
                                fontSize: '0.875rem'
                              }}
                            >
                              {threat.tags[0].replace(/_/g, ' ')}
                            </Typography>
                          ) : (
                            <Typography sx={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                              {threat.name}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={threat.type}
                            size="small"
                            sx={{
                              background: 'rgba(59, 130, 246, 0.2)',
                              color: '#3b82f6',
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ color: getSeverityColor(threat.severity) }}>
                              {getSeverityIcon(threat.severity)}
                            </Box>
                            <Chip
                              label={threat.severity}
                              size="small"
                              sx={{
                                backgroundColor: getSeverityColor(threat.severity),
                                color: 'white',
                                fontWeight: 600,
                                textTransform: 'capitalize',
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                          {threat.samples.toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                          {threat.hash}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>{threat.date}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* CTA Section */}
            <Box
              sx={{
                textAlign: 'center',
                py: 6,
                background:
                  'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
                px: 4,
                mb: 4,
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: 'var(--text-primary)',
                  fontSize: { xs: '1.75rem', md: '2.25rem' },
                }}
              >
                Ready to Access Advanced Threat Intelligence?
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  mb: 3,
                  color: 'var(--text-secondary)',
                  fontSize: '1.125rem',
                  maxWidth: '600px',
                  mx: 'auto',
                }}
              >
                Join thousands of security professionals who trust MALX for comprehensive malware
                analysis and threat intelligence.
              </Typography>

              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  component={Link}
                  to="/auth/signup"
                  variant="contained"
                  size="large"
                  sx={{
                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                    color: 'white',
                    px: 6,
                    py: 2,
                    borderRadius: 2,
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    textTransform: 'none',
                    boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                      boxShadow: '0 6px 20px rgba(239, 68, 68, 0.4)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Start Free Trial
                  <ArrowForward sx={{ ml: 1 }} />
                </Button>

                <Button
                  component={Link}
                  to="/auth/login"
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'var(--text-primary)',
                    px: 6,
                    py: 2,
                    borderRadius: 2,
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    textTransform: 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#ef4444',
                      background: 'rgba(239, 68, 68, 0.1)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  Sign In
                </Button>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Container>

      {/* Premium Footer */}
      <Box
        sx={{
          background:
            'linear-gradient(135deg, rgba(0, 0, 0, 0.95) 0%, rgba(10, 10, 15, 0.98) 100%)',
          borderTop: '1px solid rgba(239, 68, 68, 0.2)',
          mt: 4,
          backdropFilter: 'blur(25px)',
          boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.4)',
        }}
      >
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Logo and Social */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 0 } }}>
              <img
                src="/malx-login.png"
                alt="MALX"
                style={{
                  height: 160,
                  filter: 'drop-shadow(0 4px 12px rgba(239, 68, 68, 0.5))',
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <IconButton
                component="a"
                href="https://youtube.com/@pcsecuritychannel"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: '#ef4444',
                    background: 'rgba(239, 68, 68, 0.1)',
                    borderColor: 'rgba(239, 68, 68, 0.3)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <YouTube />
              </IconButton>
            </Box>
          </Box>

          {/* Footer Bottom */}
          <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '0.9rem',
              }}
            >
              © 2024 All rights reserved. Advanced threat intelligence platform.
            </Typography>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <MuiLink
                href="#"
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  transition: 'all 0.3s ease',
                  '&:hover': { color: '#ef4444' },
                }}
              >
                Privacy Policy
              </MuiLink>
              <MuiLink
                href="#"
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  transition: 'all 0.3s ease',
                  '&:hover': { color: '#ef4444' },
                }}
              >
                Terms of Service
              </MuiLink>
              <MuiLink
                href="#"
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  transition: 'all 0.3s ease',
                  '&:hover': { color: '#ef4444' },
                }}
              >
                Security
              </MuiLink>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default PublicHomepage;
